import isElementVisible from "lib/isElementVisible"

// This is required to wait for css transitions to have processed enough that screen readers think the menu is visible
// If you wish to lower this, you'll also need to adjust transition timing.
// If too low, Safari won't read the rest of the menu, since when it gained focus, much of the menu still had low opacity
const FOCUS_ON_OPEN_TIMEOUT_MS = 250
const HIDE_AND_PREVENT_SCROLL_CLASSES = ["max-lg:h-full!", "max-lg:overflow-hidden", "invisible"]

let sidebarController
const toggleSidebar = () => {
  const openButton = document.getElementById("small-sidebar-open-button")
  const closeButton = document.getElementById("small-sidebar-close-button")
  const sidebar = document.getElementById("small-sidebar")
  const outer = document.getElementById("outer")
  const footers = document.getElementById("footers")
  const isOpen = openButton?.classList?.contains("visible")

  if (isOpen) {
    // Closing the menu
    openButton?.classList.remove("visible", "opacity-0", "z-10")
    openButton?.setAttribute("aria-expanded", "false")
    openButton?.removeAttribute("aria-hidden")
    openButton?.setAttribute("tabindex", "0")
    openButton?.focus()

    closeButton?.classList.remove("opacity-100!", "z-20")
    closeButton?.setAttribute("aria-expanded", "false")
    closeButton?.setAttribute("aria-hidden", "true")
    closeButton?.setAttribute("tabindex", "-1")

    sidebar?.classList.remove(
      "visible",
      "max-lg:h-full!",
      "[&_.small-sidebar-transition-group]:top-0!",
      "[&_.small-sidebar-transition-group]:opacity-100!",
    )
    sidebar?.classList.add("invisible")
    sidebar?.setAttribute("aria-hidden", "true")

    // Make the outer & footers visible again
    outer?.classList.remove(...HIDE_AND_PREVENT_SCROLL_CLASSES)
    footers?.classList.remove(...HIDE_AND_PREVENT_SCROLL_CLASSES)
    outer?.removeAttribute("aria-hidden")
    footers?.removeAttribute("aria-hidden")

    document.removeEventListener("keydown", trapFocusInMenu)
  } else {
    // Opening the menu
    openButton?.classList.add("visible", "opacity-0", "z-10")
    openButton?.setAttribute("aria-expanded", "true")

    closeButton?.classList.add("opacity-100!", "z-20")
    closeButton?.setAttribute("aria-expanded", "true")
    closeButton?.removeAttribute("aria-hidden")
    closeButton?.setAttribute("tabindex", "0")

    sidebar?.classList.add(
      "visible",
      "max-lg:h-full!",
      "[&_.small-sidebar-transition-group]:top-0!",
      "[&_.small-sidebar-transition-group]:opacity-100!",
    )
    sidebar?.classList.remove("invisible")
    sidebar?.removeAttribute("aria-hidden")

    // Hide the outer & footers so user can't scroll them while menu is open
    outer?.classList.add(...HIDE_AND_PREVENT_SCROLL_CLASSES)
    footers?.classList.add(...HIDE_AND_PREVENT_SCROLL_CLASSES)
    outer?.setAttribute("aria-hidden", "true")
    footers?.setAttribute("aria-hidden", "true")

    // Remove any old listeners if someone is spamming the button, then add a new one
    document.removeEventListener("keydown", trapFocusInMenu)
    document.addEventListener("keydown", trapFocusInMenu)

    // Move focus to first focusable element in the menu (search input)
    setTimeout(() => {
      const searchInput = sidebar?.querySelector('input[type="search"]')
      const openButton = document.getElementById("small-sidebar-open-button")

      if (searchInput && openButton && openButton.getAttribute("aria-expanded") === "true") {
        searchInput?.focus()

        // We need to wait until focus has moved before we can update the aria-hidden
        // Browsers don't like it if you're focused on a hidden thing
        openButton?.setAttribute("aria-hidden", "true")
        openButton?.setAttribute("tabindex", "-1")
      }
    }, FOCUS_ON_OPEN_TIMEOUT_MS)
  }
}

const trapFocusInMenu = (event) => {
  if (event.key === "Tab") {
    const sidebar = document.getElementById("small-sidebar")
    const openButton = document.getElementById("small-sidebar-open-button")

    if (!sidebar || openButton?.getAttribute("aria-expanded") !== "true") {
      return
    }

    const allFocusableElements = Array.from(
      sidebar?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ) || [],
    )
    const focusableElements = allFocusableElements.filter(isElementVisible)
    const firstElement = focusableElements?.[0]
    const lastElement = focusableElements?.[focusableElements.length - 1]
    const activeElement = document.activeElement

    // If tabbing forward from last element, wrap to first
    if (!event.shiftKey && activeElement === lastElement) {
      event.preventDefault()
      firstElement?.focus()
    }
    // If shift-tabbing from first element, wrap to last
    else if (event.shiftKey && activeElement === firstElement) {
      event.preventDefault()
      lastElement?.focus()
    }
  }
}

// Monitor viewport changes to close the menu when crossing lg breakpoint (if don't do it,
// it visually disappears via css, but aria stays behind, so just properly close it.)
const handleBreakpointChange = (e) => {
  const openButton = document.getElementById("small-sidebar-open-button")

  if (e.matches && openButton?.getAttribute("aria-expanded") === "true") {
    toggleSidebar()
  }
}

const handleEscape = (event) => {
  const openButton = document.getElementById("small-sidebar-open-button")

  if (event.key === "Escape" && openButton?.getAttribute("aria-expanded") === "true") {
    event.preventDefault()
    toggleSidebar()
  }
}

export default async () => {
  const openButton = document.getElementById("small-sidebar-open-button")
  const closeButton = document.getElementById("small-sidebar-close-button")
  const regionDropdown = document.getElementById("small-region-select-dropdown")
  const sidebar = document.getElementById("small-sidebar")

  // We don't want a bunch of copies of these listeners running,
  // if there's already 1 running, then abort them before creating again
  if (sidebarController) {
    sidebarController.abort()
  }
  sidebarController = new AbortController()
  const { signal } = sidebarController // This signal for the listeners to abort

  regionDropdown?.addEventListener(
    "change",
    (event) => {
      window.location = event.target.value
    },
    { signal },
  )

  document.addEventListener("keydown", handleEscape, { signal })
  openButton?.addEventListener("click", toggleSidebar, { signal })
  closeButton?.addEventListener("click", toggleSidebar, { signal })

  // Crossed into lg breakpoint (desktop)
  // Auto-close menu (ancient browsers don't support matchMedia, so added safety)
  const lgMediaQuery = window.matchMedia?.("(min-width: 992px)")
  lgMediaQuery?.addEventListener("change", handleBreakpointChange, { signal })

  sidebar?.classList.add("invisible")
  closeButton?.setAttribute("aria-hidden", "true")
  closeButton?.setAttribute("tabindex", "-1")
}
